import { useDrag } from '@use-gesture/react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import ChevronCompactLeftIcon from '@cartwhl/ui/icons/ChevronCompactLeftIcon';
import ChevronCompactRightIcon from '@cartwhl/ui/icons/ChevronCompactRightIcon';
import Button from '@cartwhl/ui/input/Button';

export default function OrientationButtons({
  rotateX,
  rotateY,
  reset,
  x,
  setX,
}: {
  rotateX: () => void;
  rotateY: () => void;
  reset: () => void;
  x: number;
  setX: (x: number) => void;
}) {
  const bind = useDrag(
    ({ movement: [mx], active }) => {
      if (!active) return;
      setX(mx);
    },
    {
      axis: 'x',
      pointer: { capture: true },
      filterTaps: true,
      threshold: 0,
      eventOptions: { passive: false },
    },
  );

  return (
    <>
      <div
        {...bind()}
        className="absolute top-0 bottom-0 left-[calc(50%-20px)] flex w-10 items-center"
        style={{
          transform: `translateX(${x}px)`,
          background: 'linear-gradient(to bottom, #ffffff 0%, #ffffff 100%)',
          backgroundSize: '2px 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="dark:bg-gray-75 flex h-10 w-10 cursor-col-resize items-center justify-center rounded-lg bg-white">
          <ChevronCompactLeftIcon className="mr-[-3px]" />
          <ChevronCompactRightIcon className="ml-[-3px]" />
        </div>
      </div>

      <div className="absolute top-0 right-4 z-0 flex h-full flex-col justify-center gap-2">
        <Tooltip color="default" text="Character" side="left" inModal>
          <Button
            variant="secondary"
            icon={<FlipVerticalIcon />}
            onClick={rotateY}
            className="pointer-events-auto"
          />
        </Tooltip>
        <Tooltip color="default" text="Character" side="left" inModal>
          <Button
            variant="secondary"
            icon={<FlipHorizontalIcon />}
            onClick={rotateX}
            className="pointer-events-auto"
          />
        </Tooltip>
        <Tooltip color="default" text="Character" side="left" inModal>
          <Button
            variant="secondary"
            icon={<CircleArrowIcon />}
            onClick={reset}
            className="pointer-events-auto"
          />
        </Tooltip>
      </div>
    </>
  );
}

function FlipVerticalIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.33203 9.31396C1.12891 9.52132 1.02734 9.74984 1.02734 9.99951C1.03158 10.1307 1.05697 10.2513 1.10352 10.3613C1.1543 10.4756 1.23047 10.5835 1.33203 10.6851L6.22607 15.4521C6.38688 15.6172 6.58366 15.6997 6.81641 15.6997C6.97298 15.6997 7.11686 15.6616 7.24805 15.5854C7.37923 15.5093 7.48291 15.4056 7.55908 15.2744C7.63525 15.1475 7.67334 15.0057 7.67334 14.8491C7.67334 14.6121 7.58659 14.4048 7.41309 14.2271L4.04296 10.9536H15.9541L12.5859 14.2344C12.4124 14.4121 12.3257 14.6195 12.3257 14.8564C12.3257 15.013 12.3638 15.1569 12.4399 15.2881C12.5161 15.415 12.6198 15.5166 12.751 15.5928C12.8822 15.6689 13.026 15.707 13.1826 15.707C13.3011 15.707 13.409 15.688 13.5063 15.6499C13.6037 15.6076 13.6947 15.5462 13.7793 15.4658L18.667 10.686C18.8701 10.4787 18.9717 10.2502 18.9717 10.0005C18.9674 9.8693 18.9421 9.7487 18.8955 9.63867C18.8447 9.52441 18.7686 9.4165 18.667 9.31494L13.7729 4.54785C13.6121 4.38281 13.4154 4.30029 13.1826 4.30029C13.026 4.30029 12.8822 4.33838 12.751 4.41455C12.6198 4.49072 12.5161 4.5944 12.4399 4.72559C12.3638 4.85254 12.3257 4.9943 12.3257 5.15088C12.3257 5.38786 12.4124 5.59521 12.5859 5.77295L15.9968 9.08594H4.00429L7.41309 5.76562C7.58659 5.58789 7.67334 5.38053 7.67334 5.14355C7.67334 4.98698 7.63525 4.8431 7.55908 4.71191C7.48291 4.58496 7.37923 4.4834 7.24805 4.40723C7.11686 4.33105 6.97298 4.29297 6.81641 4.29297C6.69792 4.29297 6.59001 4.31201 6.49268 4.3501C6.39535 4.39242 6.30436 4.45378 6.21973 4.53418L1.33203 9.31396Z"
      />
    </svg>
  );
}

function FlipHorizontalIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.0083 1C10.258 1 10.4865 1.10156 10.6938 1.30469L15.4736 6.19238C15.554 6.27702 15.6154 6.368 15.6577 6.46533C15.6958 6.56266 15.7148 6.67057 15.7148 6.78906C15.7148 6.94564 15.6768 7.08952 15.6006 7.2207C15.5244 7.35189 15.4229 7.45557 15.2959 7.53174C15.1647 7.60791 15.0208 7.646 14.8643 7.646C14.6273 7.646 14.4199 7.55924 14.2422 7.38574L10.9614 4.01755L10.9614 15.9248L14.2349 12.5547C14.4126 12.3812 14.62 12.2944 14.8569 12.2944C15.0135 12.2944 15.1553 12.3325 15.2822 12.4087C15.4134 12.4849 15.5171 12.5885 15.5933 12.7197C15.6694 12.8509 15.7075 12.9948 15.7075 13.1514C15.7075 13.3841 15.625 13.5809 15.46 13.7417L10.6929 18.6357C10.5913 18.7373 10.4834 18.8135 10.3691 18.8643C10.2591 18.9108 10.1385 18.9362 10.0073 18.9404C9.75765 18.9404 9.52913 18.8389 9.32178 18.6357L4.54199 13.748C4.46159 13.6634 4.40023 13.5724 4.35791 13.4751C4.31982 13.3778 4.30078 13.2699 4.30078 13.1514C4.30078 12.9948 4.33887 12.8509 4.41504 12.7197C4.49121 12.5885 4.59277 12.4849 4.71973 12.4087C4.85091 12.3325 4.99479 12.2944 5.15137 12.2944C5.38835 12.2944 5.5957 12.3812 5.77344 12.5547L9.09375 15.9635L9.09375 3.9749L5.78076 7.38574C5.60303 7.55925 5.39567 7.646 5.15869 7.646C5.00212 7.646 4.86035 7.60791 4.7334 7.53174C4.60221 7.45557 4.49854 7.35189 4.42236 7.2207C4.34619 7.08952 4.30811 6.94564 4.30811 6.78906C4.30811 6.55632 4.39062 6.35954 4.55566 6.19873L9.32275 1.30469C9.42432 1.20313 9.53223 1.12695 9.64648 1.07617C9.75651 1.02962 9.87712 1.00423 10.0083 1Z"
      />
    </svg>
  );
}

function CircleArrowIcon() {
  return (
    <svg
      width="22"
      height="20"
      viewBox="0 0 22 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0.856445 10.0234L2.84033 7.23438C2.99528 7.02409 3.17236 6.92171 3.37158 6.92725C3.57633 6.92725 3.75342 7.02962 3.90283 7.23438L5.87842 10.0317C6.06657 10.2808 6.11914 10.5049 6.03613 10.7041C5.95312 10.9033 5.75391 11.0029 5.43848 11.0029H4.19336C4.30404 11.833 4.55583 12.605 4.94873 13.3188C5.34717 14.0327 5.84798 14.658 6.45117 15.1948C7.0599 15.7316 7.75163 16.1466 8.52637 16.4399C9.30111 16.7388 10.1257 16.8882 11 16.8882C11.9961 16.8882 12.9258 16.6917 13.7891 16.2988C14.6579 15.9059 15.3994 15.3719 16.0137 14.6968C16.2129 14.5031 16.4232 14.4035 16.6445 14.3979C16.8714 14.3869 17.0679 14.4588 17.2339 14.6138C17.3999 14.7632 17.4829 14.9569 17.4829 15.1948C17.4884 15.4272 17.3916 15.6431 17.1924 15.8423C16.4287 16.6779 15.5101 17.342 14.4365 17.8345C13.3685 18.3325 12.223 18.5815 11 18.5815C9.89876 18.5815 8.86117 18.3879 7.88721 18.0005C6.91325 17.6131 6.04167 17.0708 5.27246 16.3735C4.50326 15.6818 3.87516 14.8766 3.38818 13.958C2.9012 13.0394 2.59684 12.0544 2.4751 11.0029H1.29639C0.975423 11.0029 0.776204 10.9033 0.69873 10.7041C0.621257 10.5049 0.673828 10.278 0.856445 10.0234ZM4.80762 4.17139C5.57129 3.34131 6.48714 2.67725 7.55518 2.1792C8.62874 1.68115 9.77702 1.43213 11 1.43213C12.0902 1.43213 13.1222 1.62858 14.0962 2.02148C15.0757 2.40885 15.95 2.95117 16.7192 3.64844C17.4884 4.34017 18.1165 5.14535 18.6035 6.06396C19.0905 6.98258 19.3976 7.96761 19.5249 9.01904H20.6787C20.9997 9.01904 21.1989 9.11865 21.2764 9.31787C21.3594 9.51709 21.3096 9.74398 21.127 9.99854L19.1514 12.7876C18.9964 12.9979 18.8166 13.103 18.6118 13.103C18.4126 13.0975 18.2383 12.9924 18.0889 12.7876L16.105 9.99854C15.9224 9.74398 15.8698 9.51709 15.9473 9.31787C16.0303 9.11865 16.2295 9.01904 16.5449 9.01904H17.8066C17.6849 8.18896 17.4276 7.41976 17.0347 6.71143C16.6473 5.99756 16.1493 5.37223 15.5405 4.83545C14.9373 4.29867 14.2484 3.88086 13.4736 3.58203C12.6989 3.27767 11.8743 3.12549 11 3.12549C10.0094 3.12549 9.07975 3.32471 8.21094 3.72314C7.34766 4.11605 6.60612 4.6473 5.98633 5.31689C5.78711 5.51058 5.57406 5.61019 5.34717 5.61572C5.12581 5.62126 4.93213 5.54655 4.76611 5.3916C4.59456 5.24219 4.50602 5.05127 4.50049 4.81885C4.50049 4.58643 4.60286 4.37061 4.80762 4.17139Z" />
    </svg>
  );
}
